import React, { useMemo, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import EstimatItemRow from './EstimatItemRow';

/**
 * Bygg en sorterad lista av alla items (standard + custom) för en subcategory.
 */
const buildSortedSubcategoryItems = (categoryKey, subcategoryKey, subcategory, formData) => {
  const categoryData = formData.estimat?.[categoryKey] || {};
  const prefix = `${subcategoryKey}_`;

  // Standard-items
  const standardItems = (subcategory.items || []).map((cfgItem, idx) => {
    const dataKey = `${prefix}${cfgItem.name}`;
    const data = categoryData[dataKey] || {
      selected: false,
      quantity: cfgItem.defaultQuantity,
      unit: cfgItem.unit,
      price: cfgItem.price
    };
    return {
      key: dataKey,
      item: cfgItem,
      estimatItem: data,
      sortOrder: data.sortOrder ?? idx,
    };
  });

  // Custom-items
  const customKeys = Object.keys(categoryData).filter(k => k.startsWith(`${prefix}custom_`));
  const baseOrder = standardItems.length;
  const customItems = customKeys.map((ck, idx) => {
    const data = categoryData[ck] || { selected: true, quantity: 1, unit: 'st', price: 0, label: 'Ny rad', isCustom: true };
    const syntheticItem = {
      name: ck,
      label: data.label || 'Ny rad',
      unit: data.unit || 'st',
      price: data.price || 0,
      defaultQuantity: data.quantity || 1,
    };
    return {
      key: ck,
      item: syntheticItem,
      estimatItem: data,
      sortOrder: data.sortOrder ?? (baseOrder + idx),
    };
  });

  return [...standardItems, ...customItems].sort((a, b) => a.sortOrder - b.sortOrder);
};

const EstimatSubcategoryEdit = ({
  categoryKey,
  subcategoryKey,
  subcategory,
  formData,
  customerComments,
  byggkonsultResponses,
  showInlineComments,
  editingLabels,
  tempLabelValues,
  editingPrices,
  tempPriceValues,
  updateEstimat,
  updateFormData,
  handleRemoveItem,
  handleAddCustomItem,
  openAddStandardvalModal,
  startEditLabel,
  changeTempLabel,
  autoSaveLabel,
  startEditPrice,
  changeTempPrice,
  autoSavePrice,
  toggleInlineComment,
  handleEditByggkonsultEntry,
  expandedSubcategories,
  toggleSubcategory,
  onOpenInternalNoteModal,
  getInternalNote,
  hasInternalNote,
  // SubItems props
  expandedSubItems,
  toggleSubItemsExpanded,
  addSubItem,
  updateSubItem,
  removeSubItem,
  toggleUseSubItemsTotal,
  // Drag & drop
  handleReorderItems,
}) => {
  const expandedKey = `${categoryKey}_${subcategoryKey}`;
  const isExpanded = expandedSubcategories[expandedKey] !== undefined
    ? expandedSubcategories[expandedKey]
    : (subcategory.defaultExpanded || false);

  const sortedItems = useMemo(
    () => buildSortedSubcategoryItems(categoryKey, subcategoryKey, subcategory, formData),
    [categoryKey, subcategoryKey, subcategory, formData]
  );

  const onDragEnd = useCallback((result) => {
    if (!result.destination) return;
    const sortedKeys = sortedItems.map(i => i.key);
    handleReorderItems(categoryKey, subcategoryKey, sortedKeys, result.source.index, result.destination.index);
  }, [sortedItems, categoryKey, subcategoryKey, handleReorderItems]);

  return (
    <>
      {/* Subcategory header + knappar i egen tbody */}
      <tbody>
        <tr className="bg-gray-100">
          <td
            colSpan="8"
            className="px-2 sm:px-4 py-2 font-medium text-gray-900 text-sm cursor-pointer select-none"
            onClick={() => toggleSubcategory(categoryKey, subcategoryKey)}
          >
            <div className="flex items-center justify-between">
              <span>{subcategory.name}</span>
              <svg
                className={`w-4 h-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </td>
        </tr>

        <tr>
          <td colSpan="8" className="px-2 sm:px-4 py-2">
            <div className="flex justify-start gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); handleAddCustomItem(categoryKey, subcategoryKey); }}
                className="inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm rounded bg-leadme-pink hover:bg-leadme-pink-dark text-white shadow-sm"
                aria-label={`Lägg till rad i ${subcategory.name}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Lägg till
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); openAddStandardvalModal(categoryKey, subcategoryKey); }}
                className="inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm rounded bg-gray-600 hover:bg-gray-700 text-white shadow-sm"
                aria-label={`Lägg till standardval i ${subcategory.name}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
                <span className="hidden sm:inline">Lägg till standardval</span>
                <span className="sm:hidden">Standardval</span>
              </button>
            </div>
          </td>
        </tr>
      </tbody>

      {isExpanded && (
        <>
          {categoryKey === 'inredningsarkitekt' && subcategoryKey === 'design3D' ? (
            <tbody>
              <tr>
                <td colSpan="8" className="px-2 sm:px-4 py-2 sm:py-3">
                  <div className="grid grid-cols-2 gap-2">
                    {['kulörer', 'material', 'bad', 'wc', 'tvätt', 'bokhylla', 'elementskydd', 'platsbyggnation', 'belysningsplan', 'möbleringsplan'].map(key => (
                      <label key={key} className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-leadme-pink focus:ring-leadme-pink"
                          checked={!!formData.intervjuFrågor.hjälpInredningsarkitekt?.design3D?.[key]}
                          onChange={(e) => {
                            const newValue = { ...(formData.intervjuFrågor.hjälpInredningsarkitekt || {}) };
                            if (!newValue.design3D) newValue.design3D = {};
                            newValue.design3D[key] = e.target.checked;
                            updateFormData('intervjuFrågor', 'hjälpInredningsarkitekt', newValue);
                          }}
                        />
                        <span className="capitalize">{key}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Design 3D hanteras på löpande räkning och ingår inte i summeringar.</p>
                </td>
              </tr>
            </tbody>
          ) : (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId={`sub-${categoryKey}-${subcategoryKey}`}>
                {(provided) => (
                  <tbody
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="bg-white divide-y divide-gray-200"
                  >
                    {sortedItems.map((entry, index) => {
                      const isCustom = entry.key.includes('_custom_');
                      return (
                        <Draggable key={entry.key} draggableId={`${categoryKey}--${entry.key}`} index={index}>
                          {(dragProvided, snapshot) => (
                            <EstimatItemRow
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              dragHandleProps={dragProvided.dragHandleProps}
                              isDragging={snapshot.isDragging}
                              item={entry.item}
                              estimatItem={entry.estimatItem}
                              categoryKey={categoryKey}
                              subcategoryKey={isCustom ? null : subcategoryKey}
                              itemKey={`${categoryKey}_${entry.key}`}
                              customerComments={customerComments}
                              byggkonsultResponses={byggkonsultResponses}
                              showInlineComments={showInlineComments}
                              editingLabels={editingLabels}
                              tempLabelValues={tempLabelValues}
                              editingPrices={editingPrices}
                              tempPriceValues={tempPriceValues}
                              updateEstimat={updateEstimat}
                              handleRemoveItem={handleRemoveItem}
                              startEditLabel={startEditLabel}
                              changeTempLabel={changeTempLabel}
                              autoSaveLabel={autoSaveLabel}
                              startEditPrice={startEditPrice}
                              changeTempPrice={changeTempPrice}
                              autoSavePrice={autoSavePrice}
                              toggleInlineComment={toggleInlineComment}
                              handleEditByggkonsultEntry={handleEditByggkonsultEntry}
                              onOpenInternalNoteModal={onOpenInternalNoteModal}
                              getInternalNote={getInternalNote}
                              hasInternalNote={hasInternalNote}
                              isSubItemsExpanded={expandedSubItems[`${categoryKey}_${entry.key}`] || false}
                              toggleSubItemsExpanded={toggleSubItemsExpanded}
                              addSubItem={addSubItem}
                              updateSubItem={updateSubItem}
                              removeSubItem={removeSubItem}
                              toggleUseSubItemsTotal={toggleUseSubItemsTotal}
                            />
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </tbody>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </>
      )}
    </>
  );
};

export default EstimatSubcategoryEdit;
